import {
  OrderResponseDto,
  OrderFulfillBulkRequestDto,
  OrderCancelRequestDto,
  OrderChangeStatusRequestDto,
  OrderFulfillmentsDto,
} from '@tiendanube/common';
import { Status, Action } from '@tiendanube/common/src/enums';
import { ORDERS_PER_PAGE } from '../pages/constants';

export enum FilterStatusEnum {
  ALL = 'all',
  EMPTY = '',
}
export enum FilterOriginEnum {
  ALL = 'all',
  EMPTY = '',
  STORE = 'store',
  FORM = 'form',
  POS = 'pos',
  API = 'api',
  CHAT = 'chat',
}

export enum ProductCountOptionsEnum {
  ALL = 'all',
  EXACT_NUMBER = 'exactNumber',
  RANGE = 'range',
}

export type StatusType =
  | FilterStatusEnum.EMPTY
  | FilterStatusEnum.ALL
  | Status.OPEN
  | Status.CLOSED
  | Status.CANCELLED
  | Status.ARCHIVED;

export type FulfillmentStatusType =
  | FilterStatusEnum
  | Status.UNPACKED
  | Status.UNFULFILLED
  | Status.PARTIALLY_PACKED
  | Status.PARTIALLY_FULFILLED
  | Status.FULFILLED
  | Status.DELIVERED
  | Status.PICKUP_READY;

export type PaymentStatusType =
  | FilterStatusEnum.EMPTY
  | FilterStatusEnum.ALL
  | Status.PAID
  | Status.PENDING
  | Status.VOIDED
  | Status.REFUNDED
  | Status.PARTIALLY_PAID
  | Status.PARTIALLY_REFUNDED;

export type PaymentProviderType =
  | FilterStatusEnum.EMPTY
  | FilterStatusEnum.ALL
  | 'custom';

export type OriginType =
  | FilterOriginEnum.EMPTY
  | FilterOriginEnum.ALL
  | FilterOriginEnum.STORE
  | FilterOriginEnum.FORM
  | FilterOriginEnum.POS
  | FilterOriginEnum.API
  | FilterOriginEnum.CHAT;

export type FiltersType = {
  page: number;
  perPage?: string;
  q?: string;
  status: StatusType;
  dateFrom: string;
  dateTo: string;
  paymentStatus: PaymentStatusType;
  fulfillmentStatus: FulfillmentStatusType;
  paymentMethods: string;
  shippingMethod: string;
  paymentProvider: string;
  location: string;
  origin: string;
  appId: string;
  products: string; // serialized as "variantId1;variantId2;..."
  exactProductSet?: string;
  minUnits: string;
  maxUnits: string;
  isWholesale: string;
  couponIdsRaw?: string; // serialized as "couponId,couponId,..."
  stockIssues?: string;
};

export type FiltersParamsType = { page?: number } & Omit<FiltersType, 'page'>;
export type ExportFiltersType = Omit<FiltersType, 'page' | 'location'>;

export const defaultFilters: FiltersType = {
  page: 1,
  perPage: ORDERS_PER_PAGE.toString(),
  q: '',
  dateFrom: '',
  dateTo: '',
  status: FilterStatusEnum.EMPTY,
  paymentStatus: FilterStatusEnum.EMPTY,
  fulfillmentStatus: FilterStatusEnum.EMPTY,
  paymentMethods: '',
  paymentProvider: '',
  shippingMethod: '',
  location: '',
  origin: '',
  appId: '',
  products: '',
  exactProductSet: '',
  minUnits: '',
  maxUnits: '',
  isWholesale: '',
  couponIdsRaw: '',
  stockIssues: '',
};

export const defaultFilterParams: FiltersParamsType = {
  page: 1,
  perPage: ORDERS_PER_PAGE.toString(),
  q: '',
  dateFrom: '',
  dateTo: '',
  status: FilterStatusEnum.EMPTY,
  paymentStatus: FilterStatusEnum.EMPTY,
  fulfillmentStatus: FilterStatusEnum.EMPTY,
  paymentMethods: '',
  shippingMethod: '',
  paymentProvider: '',
  location: '',
  origin: '',
  appId: '',
  products: '',
  exactProductSet: '',
  minUnits: '',
  maxUnits: '',
  isWholesale: '',
  couponIdsRaw: '',
  stockIssues: '',
};

export const status: StatusType[] = [
  FilterStatusEnum.ALL,
  Status.OPEN,
  Status.CANCELLED,
  Status.ARCHIVED,
];

export const fulfillmentStatus: FulfillmentStatusType[] = [
  FilterStatusEnum.ALL,
  Status.UNPACKED,
  Status.PARTIALLY_PACKED,
  Status.UNFULFILLED,
  Status.PARTIALLY_FULFILLED,
  Status.FULFILLED,
  Status.PICKUP_READY,
  Status.DELIVERED,
];

export const paymentStatus: PaymentStatusType[] = [
  FilterStatusEnum.ALL,
  Status.PAID,
  Status.PENDING,
  Status.VOIDED,
  Status.REFUNDED,
  Status.PARTIALLY_REFUNDED,
  Status.PARTIALLY_PAID,
];

export const paymentProvider: PaymentProviderType[] = ['custom'];

// Separators for multiselect filters
export const PIPE_SEPARATOR = '|'; // For multi-select values with IDs containing commas (e.g., payment providers)
export const COMMA_SEPARATOR = ','; // Standard separator for multi-select and backend communication

export const orderOrigin: OriginType[] = [
  FilterOriginEnum.ALL,
  FilterOriginEnum.STORE,
  FilterOriginEnum.FORM,
  FilterOriginEnum.POS,
  FilterOriginEnum.API,
  FilterOriginEnum.CHAT,
];

export type ActionType =
  | OrderFulfillBulkRequestDto[]
  | OrderCancelRequestDto
  | OrderChangeStatusRequestDto
  | OrderFulfillmentsDto[];

export interface InterfaceOrderUpdateParams {
  actionType: Action;
  orderIds: string[];
  action?: ActionType;
}

export type UpdateBulkOrdersType = (
  p: InterfaceOrderUpdateParams,
) => Promise<OrderResponseDto[]>;

export const statusFilter = 'status';
export const shippingMethodFilter = 'shippingMethod';
export const couponIdsRawFilter = 'couponIdsRaw';
export const locationFilter = 'location';
export const paymentStatusFilter = 'paymentStatus';
export const fulfillmentStatusFilter = 'fulfillmentStatus';
export const paymentProviderFilter = 'paymentProvider';
export const paymentMethodsFilter = 'paymentMethods';
export const originFilter = 'origin';
export type UpdateInvoiceEventType = 'CORRECTION' | 'CANCELLATION';
